import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { CompaniesDataTable } from "@/components/companies/companies.data-table";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { companiesApi } from "@/services/companies/companies.api";
import { $fetch } from "@/lib/fetch";
import {
  type CompanyDto,
  type CompanyResponseDto,
  companiesDtoSchema,
} from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CompanyForm } from "@/components/companies/company.form";
import { ScanLogsDialog } from "@/components/scan-logs/scan-logs-dialog";

export default function CompaniesPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [companyToUpdate, setCompanyToUpdate] =
    useState<CompanyResponseDto | null>(null);
  const [logsCompany, setLogsCompany] = useState<CompanyResponseDto | null>(null);

  const {
    data: { items },
  } = useSuspenseQuery({
    queryKey: companiesApi.companies.queryKey(),
    queryFn: async () =>
      await $fetch(companiesApi.companies.route(), {
        output: companiesDtoSchema,
        throw: true,
      }),
  });

  const { mutate: createCompany, isPending: isCreatingCompany } = useMutation({
    mutationFn: async (form: CompanyDto) =>
      await $fetch(companiesApi.companies.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Wystawcę utworzono pomyślnie");
      setCreateDialogOpen(false);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu wystawcy: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: updateCompany, isPending: isUpdatingCompany } = useMutation({
    mutationFn: async ({ id, ...form }: CompanyDto & { id: number }) =>
      await $fetch(companiesApi.company.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano wystawcę");
      setCompanyToUpdate(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił bład przy aktualizacji wystawcy: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteCompany, isPending: isDeletingCompany } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(companiesApi.company.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Wystawca został usunięty");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu wystawcy: ${e.message}`);
      }
      console.error(e);
    },
  });

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">Zarządzanie wystawcami</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj wystawcę
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <CompaniesDataTable
          data={items}
          actions={{
            onUpdateClick: (company) => setCompanyToUpdate(company),
            onDeleteClick: (id) => deleteCompany({ id }),
            onLogsClick: (company) => setLogsCompany(company),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!companyToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setCompanyToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!companyToUpdate ? "Edytuj wystawcę" : "Dodaj wystawcę"}
            </DialogTitle>
          </DialogHeader>

          <CompanyForm
            onSubmit={(form) =>
              !!companyToUpdate
                ? updateCompany({ id: companyToUpdate.id, ...form })
                : createCompany(form)
            }
            defaultValues={companyToUpdate}
            companyId={companyToUpdate?.id}
            disabled={
              isCreatingCompany || isUpdatingCompany || isDeletingCompany
            }
            loading={
              isCreatingCompany || isUpdatingCompany || isDeletingCompany
            }
          />
        </DialogContent>
      </Dialog>
      <ScanLogsDialog
        filter={logsCompany ? { type: "company", companyId: logsCompany.id, title: logsCompany.title } : null}
        onClose={() => setLogsCompany(null)}
      />
    </div>
  );
}
